import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchAdminFoodSubscriptions,
  fetchAdminPickupSubscriptions,
} from './jreatsSubscriptionsApi';
import './JuniorEatsSubscriptionsPage.css';

const PAGE_SIZE = 10;
const KIND_TABS = [
  { id: 'FOOD', label: 'Food delivery' },
  { id: 'PICKUP', label: 'Pickup trips' },
];
const STATUS_OPTIONS = ['', 'ACTIVE', 'PENDING', 'EXPIRED', 'EXHAUSTED', 'CANCELLED'];

function formatMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `₹${num.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

function statusClass(status) {
  const key = String(status || '').toUpperCase();
  if (key === 'ACTIVE') return 'eats-admin-status--active';
  if (key === 'PENDING') return 'eats-admin-status--pending';
  if (key === 'EXPIRED' || key === 'CANCELLED' || key === 'EXHAUSTED') {
    return 'eats-admin-status--inactive';
  }
  return '';
}

const JuniorEatsSubscriptionsPage = () => {
  const [kind, setKind] = useState('FOOD');
  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      };
      if (status) params.status = status;
      if (search.trim()) params.q = search.trim();

      const data =
        kind === 'PICKUP'
          ? await fetchAdminPickupSubscriptions(params)
          : await fetchAdminFoodSubscriptions(params);

      setRows(Array.isArray(data.subscriptions) ? data.subscriptions : []);
      setTotal(Number(data.total) || 0);
    } catch (err) {
      setRows([]);
      setTotal(0);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load subscriptions',
      );
    } finally {
      setLoading(false);
    }
  }, [kind, page, search, status]);

  useEffect(() => {
    load();
  }, [load]);

  const creditLabel = useMemo(
    () => (kind === 'PICKUP' ? 'Trips' : 'Credits'),
    [kind],
  );

  const onSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <div className="jreats-subs-page">
      <p className="jreats-subs-intro">
        View Junior Eats customer subscriptions for meal delivery credits and pickup trip
        plans.
      </p>

      <div className="jreats-subs-kind-tabs">
        {KIND_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`jreats-subs-kind-tab${kind === tab.id ? ' is-active' : ''}`}
            onClick={() => {
              setKind(tab.id);
              setPage(1);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form className="eats-admin-search-bar jreats-subs-filters" onSubmit={onSearchSubmit}>
        <input
          type="search"
          placeholder="Search customer name, phone, email, or status"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          aria-label="Filter by status"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt || 'all'} value={opt}>
              {opt ? opt : 'All statuses'}
            </option>
          ))}
        </select>
        <button type="submit" className="eats-admin-btn eats-admin-btn--primary">
          Search
        </button>
        <button
          type="button"
          className="eats-admin-btn eats-admin-btn--ghost"
          onClick={() => {
            setSearchInput('');
            setSearch('');
            setStatus('');
            setPage(1);
          }}
        >
          Clear
        </button>
      </form>

      {error ? <div className="jreats-subs-error">{error}</div> : null}

      <div className="eats-admin-table-wrap eats-admin-table-wrap--tall">
        <table className="eats-admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Customer</th>
              <th>Subscriber</th>
              <th>Status</th>
              <th>{creditLabel}</th>
              <th>Amount</th>
              <th>Expiry</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="eats-admin-table__empty">
                  Loading subscriptions…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="eats-admin-table__empty">
                  No {kind === 'PICKUP' ? 'pickup' : 'food delivery'} subscriptions found.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={`${row.kind}-${row.subscriptionId}`}>
                  <td>#{row.subscriptionId}</td>
                  <td>
                    <div className="jreats-subs-customer">
                      <strong>{row.customerName || `Customer #${row.customerId}`}</strong>
                      <small>
                        {[row.customerContact, row.customerEmail].filter(Boolean).join(' · ') ||
                          `ID ${row.customerId}`}
                      </small>
                    </div>
                  </td>
                  <td>{row.subscriberLabel || '—'}</td>
                  <td>
                    <span className={`eats-admin-status ${statusClass(row.status)}`}>
                      {row.status || '—'}
                    </span>
                  </td>
                  <td>
                    {row.creditsRemaining ?? 0}/{row.totalCredits ?? 0}
                  </td>
                  <td>{formatMoney(row.subscriptionAmount)}</td>
                  <td>{row.expiryDate || '—'}</td>
                  <td>{row.createdDate || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="jreats-subs-pagination">
        <span>
          {total} total · Page {page} of {totalPages}
        </span>
        <div className="jreats-subs-pagination-actions">
          <button
            type="button"
            className="eats-admin-btn eats-admin-btn--ghost"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <button
            type="button"
            className="eats-admin-btn eats-admin-btn--ghost"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default JuniorEatsSubscriptionsPage;
